import { logger } from '@/services/LoggingService';
import { supabase } from '@/integrations/supabase/client';
import { getUser } from '@/services/UserService';
import { ensureValidToken } from '@/services/AuthAwareApiService';

/**
 * Service for managing Vezgo API tokens.
 * Handles token acquisition, caching, and refreshing.
 */
class TokenService {
  private tokenCache: { [clientId: string]: { token: string, expiry: number } } = {};
  private refreshPromises: { [clientId: string]: Promise<string | null> } = {};
  
  /**
   * Get a token for accessing Vezgo API
   * 
   * @param clientId - Client ID for Vezgo API
   * @param loginName - Optional login name for user identification
   * @param forceRefresh - Force refresh token even if cached token exists
   * @returns Promise resolving to the token string or null if error
   */
  async getToken(clientId: string, loginName?: string, forceRefresh: boolean = false): Promise<string | null> {
    try {
      // Check if there's already a refresh in progress for this clientId
      if (this.refreshPromises[clientId]) {
        logger.debug('TokenService', 'Another refresh already in progress, waiting for it to complete', { clientId });
        return this.refreshPromises[clientId];
      }
      
      // Check if we have a cached token that's still valid
      const cached = this.tokenCache[clientId];
      if (!forceRefresh && cached && cached.expiry > Date.now()) {
        logger.debug('TokenService', 'Using cached token', {
          clientId,
          expiresIn: Math.round((cached.expiry - Date.now()) / 1000)
        });
        return cached.token;
      }
      
      // Token not cached, expired, or refresh forced - get a new one
      logger.debug('TokenService', 'Getting fresh token', { 
        clientId,
        forceRefresh,
        hasCachedToken: !!cached,
        cacheExpired: cached ? cached.expiry <= Date.now() : false
      });
      
      // Create a refresh promise to avoid duplicate requests
      const refreshPromise = this.fetchFreshToken(clientId, loginName);
      this.refreshPromises[clientId] = refreshPromise;
      
      try {
        const token = await refreshPromise;
        return token;
      } finally {
        // Clear the promise reference after it completes, regardless of outcome
        delete this.refreshPromises[clientId];
      }
    } catch (error) {
      logger.error('TokenService', 'Error in getToken', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        clientId
      });
      return null;
    }
  }
  
  /**
   * Fetch a fresh token from the API
   * Internal method to handle the actual token acquisition
   */
  private async fetchFreshToken(clientId: string, loginName?: string): Promise<string | null> {
    try {
      // Ensure loginName present; fall back to user email if necessary.
      if (!loginName) {
        const user = await getUser();
        loginName = user?.email || 'anonymous-user';
        logger.debug('TokenService', 'Using user email as loginName', { email: loginName });
      }

      logger.debug('TokenService', 'Requesting token with loginName', {
        clientId, 
        loginName 
      });

      // Ensure camelCase params in body
      const body = { clientId, loginName };

      // Send loginName both as header and in body to maximize reliability as per edge function doc.
      const headers: Record<string, string> = {
        'loginName': loginName
      };

      logger.debug('TokenService', 'Invoking edge function with', { body, headers });

      // Ensure valid token before making API call
      await ensureValidToken('vezgo-get-token');

      // Call edge function with consistent camelCase keys and loginName in header
      const { data, error } = await supabase.functions.invoke('vezgo-get-token', {
        body,
        headers
      });

      if (error) {
        logger.error('TokenService', 'Supabase function error', {
          error: error.message, details: error
        });
        throw new Error(`Supabase function error: ${error.message}`);
      }

      logger.debug('TokenService', 'Function response received', {
        hasData: !!data, success: data?.success, hasToken: !!data?.data?.token, errorMessage: data?.error
      });

      if (!data?.success) {
        logger.error('TokenService', 'Edge function returned unsuccessful', { error: data?.error });
        throw new Error(data?.error || 'Unsuccessful token request');
      }
      if (!data?.data?.token) {
        logger.error('TokenService', 'Missing token in edge function response', { response: data });
        throw new Error('Invalid response: Missing token in response');
      }

      // Cache the token with 95% of actual expiry as documented.
      const token = data.data.token;
      const expiresIn = data.data.expiresIn || 3600;
      this.tokenCache[clientId] = {
        token: token,
        expiry: Date.now() + (expiresIn * 1000 * 0.95)
      };

      logger.debug('TokenService', 'Token retrieved and cached', {
        clientId, tokenLength: token.length, expiresIn, cacheExpiryTime: new Date(this.tokenCache[clientId].expiry).toISOString()
      });

      return token;
    } catch (error) {
      logger.error('TokenService', 'Error fetching fresh token', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        clientId
      });
      return null;
    }
  }
  
  /**
   * Clear the token cache
   * 
   * @param clientId - Optional client ID to clear specific cache entry
   */
  clearToken(clientId?: string): void {
    if (clientId) {
      delete this.tokenCache[clientId];
      logger.debug('TokenService', 'Token cache cleared for client', { clientId });
    } else {
      this.tokenCache = {};
      logger.debug('TokenService', 'All token cache cleared');
    }
  }
  
  /**
   * Check if there is a valid cached token
   * 
   * @param clientId - Client ID to check
   * @returns True if a valid token exists in the cache
   */
  hasValidToken(clientId: string): boolean {
    const cached = this.tokenCache[clientId];
    const isValid = !!(cached && cached.expiry > Date.now());
    
    logger.debug('TokenService', 'Token validity check', {
      clientId,
      hasToken: !!cached,
      isValid,
      expiresIn: cached ? Math.round((cached.expiry - Date.now()) / 1000) : 0
    });
    
    return isValid;
  }
  
  /**
   * Handle session expiry error
   * 
   * @param clientId - Client ID to refresh token for
   * @param loginName - Optional login name for user identification
   * @returns Promise resolving to refreshed token or null
   */
  async handleSessionExpiry(clientId: string, loginName?: string): Promise<string | null> {
    logger.info('TokenService', 'Handling session expiry', { clientId });
    
    // Clear any cached token
    this.clearToken(clientId);
    
    // Try to get a fresh token
    return this.getToken(clientId, loginName, true);
  }
  
  /**
   * Get the remaining time in seconds for a token
   * 
   * @param clientId - Client ID to check
   * @returns Seconds remaining or 0 if no valid token
   */
  getTokenTimeRemaining(clientId: string): number {
    const cached = this.tokenCache[clientId];
    if (!cached) return 0;
    
    const remaining = Math.max(0, Math.floor((cached.expiry - Date.now()) / 1000));
    return remaining;
  }
}

// Export the singleton instance with the correct path
export const tokenService = new TokenService();
